import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  FormHelperText,
  IconButton,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import { MuestrasChips } from '@/features/analisis/common/components/MuestrasChips';
import { ResultadoBinario } from '@/types/enums';

export interface PoolLocal {
  id: string;
  numeros_muestras: number[];
  valor: ResultadoBinario | null;
  observacion: string | null;
}

interface BaermannPoolCardProps {
  pool: PoolLocal;
  poolIndex: number;
  onChange: (updated: Partial<PoolLocal>) => void;
  onDelete: () => void;
  canDelete: boolean;
  allUsedInOtherPools: Set<number>;
  totalMuestras: number;
  showErrors: boolean;
  topeMuestrasTotal?: number;
  disabled?: boolean;
  hideMuestras?: boolean;
}

export function BaermannPoolCard({
  pool,
  poolIndex,
  onChange,
  onDelete,
  canDelete,
  allUsedInOtherPools,
  totalMuestras,
  showErrors,
  topeMuestrasTotal = 15,
  disabled = false,
  hideMuestras = false,
}: BaermannPoolCardProps): JSX.Element {
  const noMuestras = !hideMuestras && pool.numeros_muestras.length === 0;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderColor: showErrors && noMuestras ? 'warning.main' : undefined,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          Pool {poolIndex + 1}
        </Typography>
        <Tooltip title={!canDelete ? 'Debe haber al menos un pool.' : ''} placement="top">
          <span>
            <IconButton
              size="small"
              onClick={onDelete}
              disabled={!canDelete || disabled}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {!hideMuestras && (
        <Box sx={{ mb: 1.5 }}>
          <MuestrasChips
            numeros={pool.numeros_muestras}
            onChange={(numeros) => onChange({ numeros_muestras: numeros })}
            allUsedNumbers={allUsedInOtherPools}
            totalActual={totalMuestras}
            maxTotal={topeMuestrasTotal}
            disabled={disabled}
            showError={showErrors}
          />
        </Box>
      )}

      <Box sx={{ mb: 1.5 }}>
        <RadioGroup
          row
          value={pool.valor ?? ''}
          onChange={(e) => onChange({ valor: e.target.value as ResultadoBinario })}
        >
          <FormControlLabel
            value={ResultadoBinario.NEGATIVO}
            control={<Radio size="small" disabled={disabled} />}
            label="Negativo"
          />
          <FormControlLabel
            value={ResultadoBinario.POSITIVO}
            control={<Radio size="small" disabled={disabled} />}
            label="Positivo"
          />
        </RadioGroup>
        {showErrors && !pool.valor && (
          <FormHelperText error>Seleccioná un resultado</FormHelperText>
        )}
      </Box>

      <TextField
        label="Observación"
        size="small"
        fullWidth
        multiline
        rows={2}
        value={pool.observacion ?? ''}
        onChange={(e) =>
          onChange({ observacion: e.target.value !== '' ? e.target.value : null })
        }
        disabled={disabled}
      />
    </Paper>
  );
}
